"use client"

import React, { useState, useEffect, useRef, useContext } from "react"
import ApexCharts from "apexcharts"
import { styled, useTheme, ThemeProvider, createTheme } from "@mui/material/styles"
import { Container, Grid, Paper, Typography, Button, CssBaseline, useMediaQuery } from "@mui/material"
import { useTranslation } from "../common/components/LocalizationProvider"
import { LightMode, DarkMode } from "@mui/icons-material"

// Interface global para resolver o erro do ApexCharts
declare global {
  interface Window {
    ApexCharts?: {
      colors: string[]
    }
  }
}

// Definir interface para o objeto stateCounts para resolver erros de tipagem
interface StateCountsType {
  [key: string]: string[]
}

// Contexto para o tema
const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
})

// Styled components
const Root = styled("div")(({ theme }) => ({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  transition: "all 0.3s ease",
}))

const Header = styled("header")(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.primary.main,
  color: theme.palette.mode === "dark" ? theme.palette.text.primary : theme.palette.primary.contrastText,
  boxShadow: theme.shadows[4],
}))

// Melhorar o Content para ter melhor espaçamento
const Content = styled(Container)(({ theme }) => ({
  maxWidth: "100%",
  margin: "0 auto",
  padding: theme.spacing(3),
  flex: 1,
  overflow: "auto",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.97)" : "rgba(245, 245, 245, 0.97)",
}))

// Modificar o ChartContainer para ter melhor contraste no modo escuro
const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.palette.mode === "dark" ? "0 4px 20px rgba(0, 0, 0, 0.5)" : theme.shadows[4],
  backgroundColor: theme.palette.mode === "dark" ? "rgba(45, 45, 45, 0.95)" : "rgba(255, 255, 255, 0.9)",
  transition: "all 0.3s ease",
  height: "100%",
  overflow: "hidden",
  border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[300]}`,
}))

// Modify the ChartWrapper component to have a special version for the ignition chart
const ChartWrapper = styled("div")(({ theme }) => ({
  height: 350, // Aumentado de 220px para 350px
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(35, 35, 35, 0.7)" : "rgba(249, 250, 251, 0.8)",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.palette.mode === "dark" ? "inset 0 2px 10px rgba(0,0,0,0.3)" : "inset 0 2px 4px rgba(0,0,0,0.05)",
}))

// Add a special wrapper for the ignition chart with more height
const IgnitionChartWrapper = styled("div")(({ theme }) => ({
  height: 400, // Maior que os outros gráficos
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(35, 35, 35, 0.7)" : "rgba(249, 250, 251, 0.8)",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.palette.mode === "dark" ? "inset 0 2px 10px rgba(0,0,0,0.3)" : "inset 0 2px 4px rgba(0,0,0,0.05)",
}))

// Melhorar o ChartTitle para maior visibilidade
const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
  color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
  textAlign: "center",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  textShadow: theme.palette.mode === "dark" ? "0 1px 3px rgba(0,0,0,0.3)" : "none",
}))

// Melhorar o BarChartWrapper para os gráficos de barras
const BarChartWrapper = styled("div")(({ theme }) => ({
  height: 280,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(35, 35, 35, 0.7)" : theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.palette.mode === "dark" ? "inset 0 2px 10px rgba(0,0,0,0.3)" : "inset 0 2px 4px rgba(0,0,0,0.05)",
  [theme.breakpoints.up("md")]: {
    height: 320,
  },
}))

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
}))

const LoaderAnimation = styled("div")(({ theme }) => ({
  position: "relative",
  width: 80,
  height: 80,
  "& div": {
    position: "absolute",
    width: 64,
    height: 64,
    margin: 8,
    border: `8px solid ${theme.palette.primary.main}`,
    borderRadius: "50%",
    animation: "loader-animation 1.2s linear infinite",
    borderColor: `${theme.palette.primary.main} transparent transparent transparent`,
  },
  "@keyframes loader-animation": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}))

const ErrorContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
}))

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: theme.typography.h5.fontSize,
  marginBottom: theme.spacing(2),
}))

const RetryButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}))

// Componente principal do Dashboard
const Dashboard = () => {
  const theme = useTheme()
  const colorMode = useContext(ColorModeContext)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const t = useTranslation()
  const isDark = theme.palette.mode === "dark"

  // Estados para armazenar dados - sem generics
  const [devices, setDevices] = useState([])
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Refs para os gráficos - sem generics
  const alarmChartRef = useRef(null)
  const ignitionChartRef = useRef(null)
  const statesChartRef = useRef(null)
  const odometerChartRef = useRef(null)
  const hoursChartRef = useRef(null)
  const todayChartRef = useRef(null)

  // Cores para os gráficos baseadas no tema
  const primary = theme.palette.primary.main
  const secondary = theme.palette.secondary.main
  const textColor = theme.palette.text.primary

  // Configuração global para cores do ApexCharts
  useEffect(() => {
    if (window.ApexCharts) {
      window.ApexCharts.colors = [
        secondary,
        primary,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        theme.palette.success.main,
        "#546E7A",
        "#D4526E",
        "#8D5B4C",
        "#F86624",
        "#A5978B",
        "#4ECDC4",
        "#C7F464",
        "#81D4FA",
        "#FD6A02",
        "#7B1FA2",
        "#FFC300",
        "#FF5733",
        "#C70039",
        "#900C3F",
      ]
    }
  }, [theme, primary, secondary])

  // Função para buscar dados de dispositivos e posições
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar dados da API real
        let devicesData = []
        let positionsData = []

        const devicesResponse = await fetch("/api/devices")
        if (devicesResponse.ok) {
          devicesData = await devicesResponse.json()
        } else {
          throw new Error(t("errorFetchingDevices") || "Erro ao buscar dispositivos")
        }

        const positionsResponse = await fetch("/api/positions")
        if (positionsResponse.ok) {
          positionsData = await positionsResponse.json()
        } else {
          throw new Error(t("errorFetchingPositions") || "Erro ao buscar posições")
        }

        setDevices(devicesData)
        setPositions(positionsData)
        setLoading(false)
      } catch (err) {
        console.error("Erro ao carregar dados:", err)
        setError(t("errorLoadingData") || "Ocorreu um erro ao carregar os dados. Por favor, tente novamente.")
        setLoading(false)
      }
    }

    fetchData()
  }, [t])

  // Configuração base para todos os gráficos
  const getBaseChartOptions = () => ({
    chart: {
      foreColor: textColor,
      background: "transparent",
    },
    theme: {
      mode: isDark ? "dark" : "light",
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
    },
    legend: {
      labels: {
        colors: textColor,
      },
    },
    title: {
      style: {
        color: textColor,
      },
    },
    xaxis: {
      labels: {
        style: {
          colors: textColor,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: textColor,
        },
      },
    },
  })

  // GRÁFICO DE ALARMES
  useEffect(() => {
    if (!alarmChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Calcular estatísticas de alarmes
    const stateCounts: StateCountsType = {}
    // Inicializar com a chave 'Normal' para evitar erro de propriedade não existente
    stateCounts["Normal"] = []

    positions.forEach((position) => {
      if (position.attributes && position.attributes.alarm) {
        const alarmType = position.attributes.alarm
        if (!stateCounts[alarmType]) {
          stateCounts[alarmType] = []
        }
        stateCounts[alarmType].push(position.deviceId)
      } else {
        stateCounts["Normal"].push(position.deviceId)
      }
    })

    // Obter valores e chaves de forma segura
    const stateKeys = Object.keys(stateCounts)
    const stateValues = stateKeys.map((key) => (stateCounts[key] || []).length)

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      series: stateValues,
      title: {
        text: t("sharedAlarms"),
        align: "center",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300, // Aumentado de 200px para 300px
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300, // Aumentado de 200px para 300px
        fontFamily: "Helvetica, Arial, sans-serif",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: isMobile ? "bottom" : "right",
        fontSize: "14px",
        fontWeight: 600,
        markers: {
          width: 14, // Aumentado
          height: 14, // Aumentado
          radius: 7, // Aumentado
          strokeWidth: 0,
          strokeColor: "#fff",
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
        formatter: (seriesName, opts) => [seriesName, " - ", opts.w.globals.series[opts.seriesIndex]],
        labels: {
          colors: textColor,
          useSeriesColors: false,
        },
      },
      labels: stateKeys.map((key) => t(`alarm${key}`) || key),
      plotOptions: {
        pie: {
          donut: {
            size: "55%", // Reduzido para melhor visualização do gráfico maior
            background: "transparent",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "20px", // Aumentado
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 600,
                color: textColor,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: "24px", // Aumentado
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 600,
                color: textColor,
                offsetY: 16,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: t("sharedTotal") || "Total",
                fontSize: "22px", // Aumentado
                fontWeight: 700, // Mais negrito
                color: textColor,
                formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px", // Aumentado
          fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
          textShadow: "0 0 3px rgba(0,0,0,0.7)", // Sombra mais forte
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 3,
          opacity: 0.7, // Mais opaco
        },
      },
      tooltip: {
        ...getBaseChartOptions().tooltip,
        style: {
          fontSize: "13px",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
        marker: {
          show: true,
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: (_, b) => {
            if (!b || b.seriesIndex === undefined) return ""
            const key = stateKeys[b.seriesIndex]
            if (!key || !stateCounts[key]) return ""
            return stateCounts[key]
              .map((id) => {
                const device = devices.find((d) => d.id === id)
                return device ? device.name : ""
              })
              .join("<br>")
          },
          title: {
            formatter: (category) => {
              if (!category || !stateCounts[category]) return ""
              const translatedCategory = t(`alarm${category}`) || category
              return `<i>${translatedCategory} (${stateCounts[category].length})</i>`
            },
          },
        },
      },
      colors: ["#4CAF50", "#F44336", "#FFC107", "#2196F3", "#9C27B0", "#FF9800"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"], // Mais escuro no modo escuro
      },
    }

    const chart = new ApexCharts(alarmChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isMobile, textColor, isDark])

  // GRÁFICO DE IGNIÇÃO
  useEffect(() => {
    if (!ignitionChartRef.current || loading || positions.length === 0) return

    // Calcular estatísticas de ignição
    const stateCounts = { moving: 0, stopped: 0, ralenti: 0 }

    positions.forEach((position) => {
      if (position.attributes && position.attributes.ignition && !position.speed) {
        stateCounts.ralenti++
      } else if (position.attributes && position.attributes.motion) {
        stateCounts.moving++
      } else {
        stateCounts.stopped++
      }
    })

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      series: [stateCounts.moving, stateCounts.stopped, stateCounts.ralenti],
      title: {
        text: t("positionMotion"),
        align: "center",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 400,
              width: "100%",
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "pie", // Changed from donut to pie
        height: 350, // Adjusted height
        width: "100%",
        fontFamily: "Helvetica, Arial, sans-serif",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: "bottom", // Always at bottom
        fontSize: "16px", // Larger font
        fontWeight: 600,
        markers: {
          width: 16, // Larger markers
          height: 16,
          radius: 8,
          strokeWidth: 0,
          strokeColor: "#fff",
        },
        itemMargin: {
          horizontal: 15,
          vertical: 8,
        },
        formatter: (seriesName, opts) => [seriesName, " - ", opts.w.globals.series[opts.seriesIndex]],
        labels: {
          colors: textColor,
          useSeriesColors: false,
        },
      },
      // Usar as chaves de tradução corretas
      labels: [
        t("eventDeviceMoving") || "Dispositivo movendo",
        t("eventDeviceStopped") || "Dispositivo parado",
        t("eventDeviceInactive") || "Dispositivo inativo",
      ],
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
          offsetX: 0,
          offsetY: 0,
          customScale: 1,
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 10,
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px", // Larger font
          fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
          textShadow: "0 0 3px rgba(0,0,0,0.7)",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 3,
          opacity: 0.7,
        },
      },
      tooltip: {
        ...getBaseChartOptions().tooltip,
        style: {
          fontSize: "13px",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
        marker: {
          show: true,
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: (value, { seriesIndex, dataPointIndex, w }) => {
            // Traduzir o texto do tooltip
            const labels = [
              t("eventDeviceMoving") || "Dispositivo movendo",
              t("eventDeviceStopped") || "Dispositivo parado",
              t("eventDeviceInactive") || "Dispositivo inativo",
            ]
            return `${labels[seriesIndex]}: ${value}`
          },
        },
      },
      colors: ["#4CAF50", "#F44336", "#FFC107"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"], // Mais escuro no modo escuro
      },
    }

    const chart = new ApexCharts(ignitionChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [positions, loading, theme, t, isMobile, textColor, isDark])

  // GRÁFICO DE ESTADOS AGRUPADOS
  useEffect(() => {
    if (!statesChartRef.current || loading || devices.length === 0) return

    // Inicializar objeto para armazenar estados
    const stateCounts: StateCountsType = {}

    // Inicializar todas as chaves com arrays vazios
    stateCounts["Online"] = []
    stateCounts["12 horas"] = []
    stateCounts["24 horas"] = []
    stateCounts["36 horas"] = []
    stateCounts["48 horas"] = []
    stateCounts["+ 48 horas"] = []

    // Classificar dispositivos por intervalo de tempo
    devices.forEach((device) => {
      const lastUpdate = new Date().getTime() - new Date(device.lastUpdate).getTime()
      if (device.status === "online") {
        stateCounts["Online"].push(device.name)
      } else if (lastUpdate < 12 * 3600 * 1000) {
        stateCounts["12 horas"].push(device.name)
      } else if (lastUpdate < 24 * 3600 * 1000) {
        stateCounts["24 horas"].push(device.name)
      } else if (lastUpdate < 36 * 3600 * 1000) {
        stateCounts["36 horas"].push(device.name)
      } else if (lastUpdate < 48 * 3600 * 1000) {
        stateCounts["48 horas"].push(device.name)
      } else {
        stateCounts["+ 48 horas"].push(device.name)
      }
    })

    // Extrair chaves e valores de forma segura
    const stateKeys = Object.keys(stateCounts)
    // Converter arrays para contagens
    const stateSeries = stateKeys.map((key) => {
      const array = stateCounts[key] || []
      return array.length
    })

    // Traduzir as chaves de tempo
    const translatedLabels = stateKeys.map((key) => {
      if (key === "Online") return t("deviceStatusOnline")
      if (key === "12 horas") return t("deviceHours12") || "12 horas"
      if (key === "24 horas") return t("deviceHours24") || "24 horas"
      if (key === "36 horas") return t("deviceHours36") || "36 horas"
      if (key === "48 horas") return t("deviceHours48") || "48 horas"
      if (key === "+ 48 horas") return t("deviceHoursMore48") || "+ 48 horas"
      return key
    })

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
//       tooltip: {
//         ...getBaseChartOptions().tooltip,
//         style: {
//           fontSize: "13px",
//           fontFamily: "Helvetica, Arial, sans-serif",
//         },
//         marker: {
//           show: true,
//         },
//         fixed: {
//           enabled: false,
//           position: "topRight",
//           offsetX: 0,
//           offsetY: 0,
//         },
//         y: {
//           formatter: (_, b) => {
//             if (!b || b.seriesIndex === undefined) return ""
//             const key = stateKeys[b.seriesIndex]
//             if (!key || !stateCounts[key]) return ""
//             return stateCounts[key].join("<br>")
//           },
//           title: {
//             formatter: (category) => {
//               if (!category || !stateCounts[category]) return ""
//               // Traduzir a categoria no tooltip
//               const translatedCategory = translatedLabels[stateKeys.indexOf(category)]
//               return `<i>${translatedCategory} (${stateCounts[category].length})</i>`
//             },
//           },
//         },
//       },
      series: stateSeries,
      title: {
        text: t("deviceLastUpdate"),
        align: "center",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300, // Aumentado de 200px para 300px
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300, // Aumentado de 200px para 300px
        fontFamily: "Helvetica, Arial, sans-serif",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: isMobile ? "bottom" : "right",
        fontSize: "14px",
        fontWeight: 600,
        markers: {
          width: 14, // Aumentado
          height: 14, // Aumentado
          radius: 7, // Aumentado
          strokeWidth: 0,
          strokeColor: "#fff",
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
        formatter: (seriesName, opts) => [seriesName, " - ", opts.w.globals.series[opts.seriesIndex]],
        labels: {
          colors: textColor,
          useSeriesColors: false,
        },
      },
      labels: translatedLabels,
      plotOptions: {
        pie: {
          donut: {
            size: "55%", // Reduzido para melhor visualização do gráfico maior
            background: "transparent",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "20px", // Aumentado
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 600,
                color: textColor,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: "24px", // Aumentado
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 600,
                color: textColor,
                offsetY: 16,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: t("sharedTotal") || "Total",
                fontSize: "22px", // Aumentado
                fontWeight: 700, // Mais negrito
                color: textColor,
                formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px", // Aumentado
          fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
          textShadow: "0 0 3px rgba(0,0,0,0.7)", // Sombra mais forte
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 3,
          opacity: 0.7, // Mais opaco
        },
      },
      tooltip: {
        ...getBaseChartOptions().tooltip,
        style: {
          fontSize: "13px",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
        marker: {
          show: true,
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: (_, b) => {
            if (!b || b.seriesIndex === undefined) return ""
            const key = stateKeys[b.seriesIndex]
            if (!key || !stateCounts[key]) return ""
            return stateCounts[key].join("<br>")
          },
          title: {
            formatter: (category) => {
              if (!category || !stateCounts[category]) return ""
              const translatedCategory = translatedLabels[stateKeys.indexOf(category)]
              return `<i>${translatedCategory} (${stateCounts[category].length})</i>`
            },
          },
        },
       },
      colors: ["#4CAF50", "#2196F3", "#FFC107", "#FF9800", "#F44336", "#9C27B0"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"], // Mais escuro no modo escuro
      },
    }

    const chart = new ApexCharts(statesChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, loading, theme, t, isMobile, textColor, isDark])

  // GRÁFICO DE ODÔMETRO
  useEffect(() => {
    if (!odometerChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Função para obter o valor do odômetro
    const getValue = (p, d) => {
      return Math.round(
        ((d && d.attributes && !d.attributes["report.ignoreOdometer"] && p.attributes && p.attributes.odometer) ||
          (p.attributes && p.attributes.totalDistance) ||
          0) / 1000,
      )
    }

    // Filtrar e ordenar dados
    const data = positions
      .filter(
        (p) =>
          getValue(
            p,
            devices.find((d) => d.id === p.deviceId),
          ) > 0,
      )
      .sort((a, b) => {
        const deviceA = devices.find((d) => d.id === a.deviceId)
        const deviceB = devices.find((d) => d.id === b.deviceId)
        return getValue(b, deviceB) - getValue(a, deviceA)
      })
      .slice(0, 20)

    // Preparar dados para o gráfico
    const chartData = data.map((p) => {
      const device = devices.find((d) => d.id === p.deviceId)
      return getValue(p, device)
    })

    const categoryLabels = data.map((p) => {
      const device = devices.find((d) => d.id === p.deviceId)
      return device ? device.name : ""
    })

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      title: {
        text: t("positionOdometer"),
        align: "center",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          color: textColor,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
              },
            },
            chart: {
              height: 250,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      series: [
        {
          name: t("positionOdometer"),
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: "100%",
        width: "100%",
        fontFamily: "Helvetica, Arial, sans-serif",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          borderRadiusApplication: "end",
          barHeight: "80%",
          distributed: true,
          horizontal: true,
          dataLabels: {
            position: "center",
          },
        },
      },
      legend: {
        show: false,
      },
      colors: [
        "#4CAF50",
        "#2196F3",
        "#FFC107",
        "#FF9800",
        "#F44336",
        "#9C27B0",
        "#00BCD4",
        "#795548",
        "#607D8B",
        "#3F51B5",
        "#CDDC39",
        "#009688",
        "#E91E63",
        "#673AB7",
        "#8BC34A",
        "#FF5722",
        "#03A9F4",
        "#FFEB3B",
        "#9E9E9E",
        "#FF4081",
      ],
      stroke: {
        width: 1,
        colors: [isDark ? "#333" : "#fff"],
      },
      grid: {
        show: true,
        borderColor: isDark ? "#333" : "#e0e0e0",
        strokeDashArray: 3,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: ["#fff"],
          fontWeight: "bold",
          fontSize: "14px",
          fontFamily: "Helvetica, Arial, sans-serif",
          textShadow: "0 0 3px rgba(0,0,0,0.5)",
        },
        formatter: (val, opt) => {
          if (!opt || !opt.w || !opt.w.globals || !opt.w.globals.labels) return ""
          const index = opt.dataPointIndex
          if (index === undefined || !categoryLabels[index]) return ""
          return categoryLabels[index] + ":  " + val + " km"
        },
        offsetX: 10,
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 3,
          opacity: 0.5,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      xaxis: {
        categories: categoryLabels,
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: 600,
            colors: textColor,
          },
        },
        title: {
          text: t("positionOdometerKm") || "Quilômetros",
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
        axisBorder: {
          show: true,
          color: isDark ? "#555" : "#78909C",
        },
        axisTicks: {
          show: true,
          color: isDark ? "#555" : "#78909C",
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            return `${val} ${t("sharedKm") || "km"}`
          },
          title: {
            formatter: (seriesName) => {
              return t("positionOdometer")
            },
          },
        },
        theme: isDark ? "dark" : "light",
        style: {
          fontSize: "12px",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
      },
    }

    const chart = new ApexCharts(odometerChartRef.current, options)
    chart.render()

    // Atualizar opções depois de um curto período
    const timer = setTimeout(() => chart.updateOptions({}, true), 1)

    return () => {
      clearTimeout(timer)
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isDark, textColor])

  // GRÁFICO DE HORAS
  useEffect(() => {
    if (!hoursChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Função para obter o valor de horas
    const getValue = (p) => {
      return p.attributes && p.attributes.hours ? Math.round(p.attributes.hours / 1000 / 3600) : 0
    }

    // Filtrar e ordenar dados
    const data = positions
      .filter((p) => getValue(p) > 0)
      .sort((a, b) => getValue(b) - getValue(a))
      .slice(0, 20)

    // Preparar dados para o gráfico
    const chartData = data.map((p) => getValue(p))

    const categoryLabels = data.map((p) => {
      const device = devices.find((d) => d.id === p.deviceId)
      return device ? device.name : ""
    })

    // Modificar as opções do gráfico de horas para melhor visibilidade
    const options = {
      ...getBaseChartOptions(),
      title: {
        text: t("positionHours"),
        align: "center",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          color: textColor,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
              },
            },
            chart: {
              height: 250,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      series: [
        {
          name: t("positionHours"),
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: "100%",
        width: "100%",
        fontFamily: "Helvetica, Arial, sans-serif",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          borderRadiusApplication: "end",
          barHeight: "80%",
          distributed: true,
          horizontal: true,
          dataLabels: {
            position: "center",
          },
        },
      },
      legend: {
        show: false,
      },
      colors: [
        "#F44336",
        "#2196F3",
        "#FFC107",
        "#4CAF50",
        "#9C27B0",
        "#FF9800",
        "#00BCD4",
        "#795548",
        "#607D8B",
        "#3F51B5",
        "#CDDC39",
        "#009688",
        "#E91E63",
        "#673AB7",
        "#8BC34A",
        "#FF5722",
        "#03A9F4",
        "#FFEB3B",
        "#9E9E9E",
        "#FF4081",
      ],
      stroke: {
        width: 1,
        colors: [isDark ? "#333" : "#fff"],
      },
      grid: {
        show: true,
        borderColor: isDark ? "#333" : "#e0e0e0",
        strokeDashArray: 3,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: ["#fff"],
          fontWeight: "bold",
          fontSize: "14px",
          fontFamily: "Helvetica, Arial, sans-serif",
          textShadow: "0 0 3px rgba(0,0,0,0.5)",
        },
        formatter: (val, opt) => {
          if (!opt || !opt.w || !opt.w.globals || !opt.w.globals.labels) return ""
          const index = opt.dataPointIndex
          if (index === undefined || !categoryLabels[index]) return ""
          return categoryLabels[index] + ":  " + val + " h"
        },
        offsetX: 10,
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 3,
          opacity: 0.5,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      xaxis: {
        categories: categoryLabels,
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: 600,
            colors: textColor,
          },
        },
        title: {
          text: t("sharedHours") || "Horas",
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
        axisBorder: {
          show: true,
          color: isDark ? "#555" : "#78909C",
        },
        axisTicks: {
          show: true,
          color: isDark ? "#555" : "#78909C",
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            return `${val} ${t("sharedHours") || "horas"}`
          },
          title: {
            formatter: (seriesName) => {
              return t("positionHours")
            },
          },
        },
        theme: isDark ? "dark" : "light",
        style: {
          fontSize: "12px",
          fontFamily: "Helvetica, Arial, sans-serif",
        },
      },
    }

    const chart = new ApexCharts(hoursChartRef.current, options)
    chart.render()

    // Atualizar opções depois de um curto período
    const timer = setTimeout(() => chart.updateOptions({}, true), 1)

    return () => {
      clearTimeout(timer)
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isDark])

  // GRÁFICO DE ATIVIDADES
  useEffect(() => {
    if (!todayChartRef.current || loading || devices.length === 0) return

    // Datas para o período de hoje
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const to = from + 1000 * 60 * 60 * 24

    // Ordenar e limitar dispositivos para melhor visualização
    const _devices = [...devices].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 30)

    // Opções iniciais do gráfico
    const options = {
      ...getBaseChartOptions(),
      noData: {
        text: t("sharedLoading") + "...",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: textColor,
          fontSize: "24px",
          fontFamily: undefined,
        },
      },
      series: [],
      title: {
        text: t("reportTodayActivity"),
        align: "left",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
              },
            },
            chart: {
              height: 250,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "rangeBar",
        height: "100%",
        fontFamily: "Helvetica, Arial, sans-serif",
      },
      plotOptions: {
        bar: {
          barHeight: "80%",
          horizontal: true,
          rangeBarGroupRows: true,
          borderRadius: 2,
        },
      },
      fill: {
        type: "solid",
        opacity: 0.8,
      },
      xaxis: {
        min: from,
        max: to,
        type: "datetime",
        labels: {
          datetimeUTC: false,
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        title: {
          // text: t("reportTime") || "Horário",
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        fontSize: "14px",
        fontWeight: 600,
        markers: {
          width: 12,
          height: 12,
          radius: 6,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        ...getBaseChartOptions().tooltip,
        custom: (opts) => {
          if (!opts || !opts.y1 || !opts.y2) return ""
          const fromTime = new Date(opts.y1).toLocaleString()
          const toTime = new Date(opts.y2).toLocaleTimeString()
          const duration = Math.round((opts.y2 - opts.y1) / (1000 * 60)) // duração em minutos

          let durationText = ""
          if (duration >= 60) {
            const hours = Math.floor(duration / 60)
            const minutes = duration % 60
            durationText = `${hours}h ${minutes}m`
          } else {
            durationText = `${duration}m`
          }

          return `
          <div style="padding: 8px; font-size: 12px; font-family: Helvetica, Arial, sans-serif;">
            <div><b>${t("reportFrom") || "De"}:</b> ${fromTime}</div>
            <div><b>${t("reportTo") || "Até"}:</b> ${toTime}</div>
            <div><b>${t("reportDuration") || "Duração"}:</b> ${durationText}</div>
          </div>
        `
        },
      },
    }

    const chart = new ApexCharts(todayChartRef.current, options)
    chart.render()

    // Buscar dados de viagens e paradas da API
    const fetchTripsAndStops = async () => {
      try {
        const deviceIds = _devices.map((d) => `deviceId=${d.id}`).join("&")
        const dateParam = `&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`

        const [tripsResponse, stopsResponse] = await Promise.all([
          fetch(`/api/reports/trips?${deviceIds}${dateParam}`, {
            headers: { Accept: "application/json" },
          }),
          fetch(`/api/reports/stops?${deviceIds}${dateParam}`, {
            headers: { Accept: "application/json" },
          }),
        ])

        if (tripsResponse.ok && stopsResponse.ok) {
          const trips = await tripsResponse.json()
          const stops = await stopsResponse.json()

          // Preparar as séries de dados para o gráfico
          // const deviceSeries = _devices.map((d) => ({
          //   name: "dummy",
          //   data: [{ x: d.name }],
          // }))

          const stopsSeries = {
            color: theme.palette.primary.main,
            name: t("reportStops") || "Paradas",
            data: stops.map((s) => {
              const deviceName = _devices.find((d) => d.id === s.deviceId)?.name || ""
              return {
                x: deviceName,
                y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()],
              }
            }),
          }

          const tripsSeries = {
            color: theme.palette.secondary.main,
            name: t("reportTrips") || "Viagens",
            data: trips.map((s) => {
              const deviceName = _devices.find((d) => d.id === s.deviceId)?.name || ""
              return {
                x: deviceName,
                y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()],
              }
            }),
          }

          // Atualizar opções do gráfico
          chart.updateOptions(
            {
              series: [stopsSeries, tripsSeries],
              xaxis: {
                min: from,
                max: to,
              },
            },
            true,
            true,
          )
        } else {
          console.error(t("errorFetchingTripsStops") || "Erro ao buscar viagens ou paradas")
        }
      } catch (error) {
        console.error(t("errorFetchingData") || "Erro ao buscar dados:", error)
      }
    }

    // Buscar dados
    fetchTripsAndStops()

    return () => {
      chart.destroy()
    }
  }, [devices, loading, theme, t])

  // Exibir indicador de carregamento
  if (loading) {
    return (
      <LoaderContainer>
        <LoaderAnimation>
          <div></div>
        </LoaderAnimation>
      </LoaderContainer>
    )
  }

  // Exibir mensagem de erro
  if (error) {
    return (
      <ErrorContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={() => window.location.reload()}>{t("sharedRetry")}</RetryButton>
      </ErrorContainer>
    )
  }

  // Renderizar dashboard
  return (
    <Root>
      <Header>
        <Typography variant="h5" component="h1">
          {t("reportDashboard")}
        </Typography>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={colorMode.toggleColorMode}
            startIcon={isDark ? <LightMode /> : <DarkMode />}
            style={{
              borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            }}
          >
            {isDark ? t("themeLightMode") || "Modo Claro" : t("themeDarkMode") || "Modo Escuro"}
          </Button>
        </div>
      </Header>
      <Content>
        {/* Melhorar o Grid para ter melhor espaçamento */}
        <Grid container spacing={3}>
          {/* First row: Three donut charts in a row */}
          <Grid item xs={12} md={4}>
            <ChartContainer>
              <ChartTitle>{t("deviceLastUpdate")}</ChartTitle>
              <ChartWrapper ref={statesChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={4}>
            <ChartContainer>
              <ChartTitle>{t("positionMotion")}</ChartTitle>
              <IgnitionChartWrapper ref={ignitionChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={4}>
            <ChartContainer>
              <ChartTitle>{t("sharedAlarms")}</ChartTitle>
              <ChartWrapper ref={alarmChartRef} />
            </ChartContainer>
          </Grid>

          {/* Second row: Odometer and Hours charts side by side */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <ChartTitle>{t("positionOdometer")}</ChartTitle>
              <BarChartWrapper ref={odometerChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartContainer>
              <ChartTitle>{t("positionHours")}</ChartTitle>
              <BarChartWrapper ref={hoursChartRef} />
            </ChartContainer>
          </Grid>

          {/* Third row: Activity chart */}
          <Grid item xs={12}>
            <ChartContainer>
              <ChartTitle>{t("reportTodayActivity")}</ChartTitle>
              <BarChartWrapper ref={todayChartRef} />
            </ChartContainer>
          </Grid>
        </Grid>
      </Content>
    </Root>
  )
}

// Atualizar o componente DashboardWithTheme para detectar o modo do sistema
const DashboardWithTheme = () => {
  // Detectar preferência do sistema para modo escuro
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
  const [mode, setMode] = React.useState<"light" | "dark">(prefersDarkMode ? "dark" : "light")

  // Efeito para atualizar o modo quando a preferência do sistema mudar
  React.useEffect(() => {
    setMode(prefersDarkMode ? "dark" : "light")
  }, [prefersDarkMode])

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"))
      },
    }),
    [],
  )

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "dark" ? "#90caf9" : "#1976D2",
          },
          secondary: {
            main: mode === "dark" ? "#81c784" : "#4CAF50",
          },
          background: {
            default: mode === "dark" ? "#121212" : "#f5f5f5",
            paper: mode === "dark" ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [mode],
  )

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Dashboard />
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export default DashboardWithTheme

